import { el, onTeardown } from '../lib/dom.js';
import { loadSnapshot } from '../data/snapshot.js';
import { createPinball } from '../lib/pinball.js';
import {
  pinball,
  recordPinball,
  recordCatch,
  togglePinballSound,
  togglePinballHaptics,
} from '../store/pinball.js';
import { Sprite } from '../components/Sprite.js';

const pretty = (name) => name.replace(/-/g, ' ');

// --- tiny WebAudio blips (no assets) ---------------------------------
let actx = null;
function tone(freq, dur, type = 'square', vol = 0.05, slideTo) {
  if (!pinball.get().sound) return;
  try {
    actx = actx || new (window.AudioContext || window.webkitAudioContext)();
    if (actx.state === 'suspended') actx.resume();
    const t = actx.currentTime;
    const o = actx.createOscillator();
    const g = actx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(actx.destination);
    o.start(t);
    o.stop(t + dur + 0.02);
  } catch {
    /* audio not available — no-op */
  }
}
const sfx = {
  bump: () => tone(320 + Math.random() * 120, 0.06),
  post: () => tone(520, 0.04, 'triangle', 0.035),
  drain: () => tone(300, 0.35, 'sawtooth', 0.05, 90),
  launch: () => tone(180, 0.22, 'sawtooth', 0.05, 520),
  catch: () => {
    [523, 659, 784, 1047].forEach((f, i) =>
      setTimeout(() => tone(f, 0.12, 'square', 0.05), i * 90),
    );
  },
};

// --- haptics (Android / supporting browsers; a no-op elsewhere) -------
const canVibrate = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
let lastBuzz = 0;
function buzz(pattern, force) {
  if (!canVibrate || !pinball.get().haptics) return;
  const now = performance.now();
  if (!force && now - lastBuzz < 24) return; // don't stack up during a rattle
  lastBuzz = now;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* vibration not available — no-op */
  }
}
const haptics = {
  bump: () => buzz(14),
  post: () => buzz(7),
  launch: () => buzz(22, true),
  drain: () => buzz([45, 35, 70], true),
  catch: () => buzz([16, 40, 16, 40, 45], true),
  over: () => buzz([80, 60, 80], true),
};

/**
 * Hidden mini-game — a single Pokémon-flavoured pinball table. Reached from
 * the "Who's that Pokémon?" screen's nav row, or the bare #/pinball hash.
 *
 * ← / → (or A / L / Z) work the flippers; hold Space or the Launch button
 * to pull the plunger. On touch, tap the table's left or right half.
 */
export async function PinballView() {
  const view = el('section', { class: 'view pin' });
  const snap = await loadSnapshot();

  const scoreEl = el('span', { class: 'pin__score' }, '0');
  const bestEl = el('span', { class: 'pin__best' }, `Best ${pinball.get().best.toLocaleString()}`);
  const ballsEl = el('span', { class: 'pin__balls' });
  const overlay = el('div', { class: 'pin__over', hidden: true });
  const catchPop = el('div', { class: 'pin__catch', hidden: true });
  const caughtStrip = el('div', { class: 'pin__caught' });
  const canvas = el('canvas', { class: 'pin__canvas', 'aria-label': 'Pinball table' });

  function renderBalls(n) {
    ballsEl.textContent = '●'.repeat(Math.max(0, n)) || '—';
  }
  function renderCaught() {
    const list = pinball.get().caught;
    caughtStrip.replaceChildren(
      list.length
        ? el(
            'div',
            { class: 'pin__caught-row' },
            el('span', { class: 'pin__caught-label' }, 'Caught'),
            ...list.slice(0, 14).map((m) => Sprite(m.id, { size: 30, alt: m.name })),
          )
        : el('span', { class: 'pin__caught-empty' }, 'Light all 5 Poké Balls to catch one.'),
    );
  }

  let catchTimer = null;
  let launchBtn; // created below (it needs `game`); onCharge only fires after start()
  const soundBtn = el('button', {
    class: 'pin__sound',
    type: 'button',
    title: 'Sound',
    'aria-label': 'Toggle sound',
    onclick: () => {
      togglePinballSound();
      soundBtn.textContent = pinball.get().sound ? '🔊' : '🔇';
    },
  });
  soundBtn.textContent = pinball.get().sound ? '🔊' : '🔇';

  const hapticBtn = canVibrate
    ? el('button', {
        class: 'pin__sound',
        type: 'button',
        title: 'Vibration',
        'aria-label': 'Toggle vibration',
        onclick: () => {
          togglePinballHaptics();
          const on = pinball.get().haptics;
          hapticBtn.textContent = on ? '📳' : '📴';
          if (on) navigator.vibrate(20);
        },
      })
    : null;
  if (hapticBtn) hapticBtn.textContent = pinball.get().haptics ? '📳' : '📴';

  const game = createPinball(canvas, {
    onScore: (s) => (scoreEl.textContent = s.toLocaleString()),
    onBalls: renderBalls,
    onBump: (kind) => {
      if (kind === 'post') {
        sfx.post();
        haptics.post();
      } else {
        sfx.bump();
        haptics.bump();
      }
    },
    onDrain: () => {
      sfx.drain();
      haptics.drain();
    },
    onCharge: (c) => launchBtn.style.setProperty('--charge', c.toFixed(3)),
    pickMon: () => {
      const s = snap.species[(Math.random() * snap.species.length) | 0];
      return { id: s.id, name: pretty(s.name) };
    },
    onCatch: (mon) => {
      sfx.catch();
      haptics.catch();
      recordCatch(mon);
      renderCaught();
      catchPop.replaceChildren(
        Sprite(mon.id, { style: 'official', size: 120, alt: mon.name }),
        el('p', { class: 'pin__catch-title' }, 'Gotcha!'),
        el('p', { class: 'pin__catch-name' }, `${mon.name} was caught!`),
      );
      catchPop.hidden = false;
      clearTimeout(catchTimer);
      catchTimer = setTimeout(() => (catchPop.hidden = true), 2200);
    },
    onGameOver: (score) => {
      recordPinball(score);
      haptics.over();
      bestEl.textContent = `Best ${pinball.get().best.toLocaleString()}`;
      overlay.replaceChildren(
        el('p', { class: 'pin__over-score' }, `${score.toLocaleString()} points`),
        el(
          'button',
          {
            class: 'button',
            type: 'button',
            onclick: () => {
              overlay.hidden = true;
              game.newGame();
            },
          },
          'Play again',
        ),
      );
      overlay.hidden = false;
    },
  });

  // --- input --------------------------------------------------------
  const KEYS = { ArrowLeft: 'L', ArrowRight: 'R', a: 'L', d: 'R', z: 'L', l: 'R' };
  function onKey(down) {
    return (e) => {
      if (e.repeat) return;
      const f = KEYS[e.key];
      if (f) {
        game.setFlipper(f, down);
        e.preventDefault();
      } else if (e.key === ' ' || e.key === 'ArrowDown') {
        if (down && !e.repeat) {
          sfx.launch();
          haptics.launch();
        }
        game.setPlunge(down);
        e.preventDefault();
      }
    };
  }
  const kd = onKey(true);
  const ku = onKey(false);
  window.addEventListener('keydown', kd);
  window.addEventListener('keyup', ku);

  // TEMPORARY dev entrance — hold A+B+C+D together to jump to the board
  // editor (see PinballEditorView.js). No visible link; remove this with
  // the editor once the table layout is settled.
  const heldKeys = new Set();
  const editorCombo = ['a', 'b', 'c', 'd'];
  const onComboKey = (e) => {
    const key = e.key.toLowerCase();
    if (!editorCombo.includes(key)) return;
    heldKeys.add(key);
    if (editorCombo.every((k) => heldKeys.has(k))) location.hash = '#/pinball-editor';
  };
  const onComboKeyUp = (e) => heldKeys.delete(e.key.toLowerCase());
  window.addEventListener('keydown', onComboKey);
  window.addEventListener('keyup', onComboKeyUp);

  // Per-pointer flipper tracking: two thumbs work, and a missed pointerup
  // can never leave a flipper stuck on.
  const pointers = new Map(); // pointerId -> 'L' | 'R'
  function resync() {
    let L = false;
    let R = false;
    for (const side of pointers.values()) side === 'L' ? (L = true) : (R = true);
    game.setFlipper('L', L);
    game.setFlipper('R', R);
  }
  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    canvas.setPointerCapture?.(e.pointerId);
    const rect = canvas.getBoundingClientRect();
    pointers.set(e.pointerId, e.clientX - rect.left < rect.width / 2 ? 'L' : 'R');
    resync();
  });
  const drop = (e) => {
    if (pointers.delete(e.pointerId)) resync();
  };
  for (const ev of ['pointerup', 'pointercancel', 'pointerleave', 'lostpointercapture'])
    canvas.addEventListener(ev, drop);

  launchBtn = el('button', {
    class: 'button pin__launch',
    type: 'button',
    onpointerdown: (e) => {
      e.preventDefault();
      sfx.launch();
      haptics.launch();
      game.setPlunge(true);
    },
  });
  launchBtn.append(
    el('span', { class: 'pin__launch-fill' }),
    el('span', { class: 'pin__launch-label' }, 'Hold to launch'),
  );
  const releasePlunge = () => game.setPlunge(false);
  for (const ev of ['pointerup', 'pointerleave', 'pointercancel'])
    launchBtn.addEventListener(ev, releasePlunge);

  // Global safety net: any release / focus loss drops the plunger and flippers.
  const panic = () => {
    game.setPlunge(false);
    if (pointers.size) {
      pointers.clear();
      resync();
    }
  };
  const onVis = () => document.hidden && panic();
  window.addEventListener('pointerup', releasePlunge);
  window.addEventListener('pointercancel', panic);
  window.addEventListener('blur', panic);
  document.addEventListener('visibilitychange', onVis);

  view.append(
    el(
      'nav',
      { class: 'lookup__nav' },
      el('a', { class: 'link', href: '#/' }, '‹ Dex'),
      el('span', {}, ' · '),
      el('a', { class: 'link', href: '#/whos-that' }, "Who's that Pokémon?"),
    ),
    el('header', { class: 'view__header' }, el('h1', {}, 'Pinball')),
    el(
      'div',
      { class: 'pin__hud' },
      el('span', {}, 'Score ', scoreEl),
      ballsEl,
      bestEl,
      soundBtn,
      hapticBtn,
    ),
    el('div', { class: 'pin__stage' }, canvas, overlay, catchPop),
    caughtStrip,
    el('div', { class: 'pin__controls' }, launchBtn),
    el(
      'p',
      { class: 'pin__hint' },
      '← / → flippers · hold Space to launch · on touch, tap the table’s left / right half',
    ),
  );

  renderBalls(3);
  renderCaught();
  requestAnimationFrame(() => game.start());

  const offStore = pinball.subscribe(() => renderCaught());

  onTeardown(view, () => {
    game.stop();
    clearTimeout(catchTimer);
    offStore();
    window.removeEventListener('keydown', kd);
    window.removeEventListener('keyup', ku);
    window.removeEventListener('keydown', onComboKey);
    window.removeEventListener('keyup', onComboKeyUp);
    window.removeEventListener('pointerup', releasePlunge);
    window.removeEventListener('pointercancel', panic);
    window.removeEventListener('blur', panic);
    document.removeEventListener('visibilitychange', onVis);
  });
  return view;
}
